import React, { Suspense, lazy, useEffect } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import Navbar from './components/Navbar'
import Footer from './components/Footer'
import LiveChat from './components/LiveChat'
import CookieConsent from './components/CookieConsent'
import ScrollToTop from './components/ScrollToTop'
import AnchorScrollHandler from './components/AnchorScrollHandler'
import PromoBanner from './components/PromoBanner'
import SEORouter from './components/SEORouter'
import ErrorBoundary from './components/ErrorBoundary'
import { ToastProvider } from './components/ui/Toasts'
import AuthGuard from './components/guards/AuthGuard'
import AdminGuard from './components/guards/AdminGuard'
import RedirectIfAuthed from './components/guards/RedirectIfAuthed'

// Pages — lazy loaded for code splitting
const Home = lazy(() => import('./pages/Home'))
const Pricing = lazy(() => import('./pages/Pricing'))
const ProductPricing = lazy(() => import('./pages/ProductPricing'))
const Checkout = lazy(() => import('./pages/Checkout'))
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'))
const PaymentFailed = lazy(() => import('./pages/PaymentFailed'))
const Account = lazy(() => import('./pages/Account'))
const Company = lazy(() => import('./pages/Company'))
const Products = lazy(() => import('./pages/Products'))
const Legal = lazy(() => import('./pages/Legal'))
const NotFound = lazy(() => import('./pages/NotFound'))
const Login = lazy(() => import('./pages/Login'))
const LoginAdmin = lazy(() => import('./pages/LoginAdmin'))
const Signup = lazy(() => import('./pages/Signup'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Support = lazy(() => import('./pages/Support'))
const SupportAdmin = lazy(() => import('./pages/SupportAdmin'))
const Admin = lazy(() => import('./pages/Admin'))
const Notifications = lazy(() => import('./pages/Notifications'))
const Marketplace = lazy(() => import('./pages/Marketplace'))
const Wishlist = lazy(() => import('./pages/Wishlist'))
const Cart = lazy(() => import('./pages/Cart'))
const MarketplaceDetail = lazy(() => import('./pages/MarketplaceDetail'))
const Publish = lazy(() => import('./pages/Publish'))
const Publisher = lazy(() => import('./pages/Publisher'))
const Demo = lazy(() => import('./pages/Demo'))
const DemoSchedule = lazy(() => import('./pages/DemoSchedule'))
const Build = lazy(() => import('./pages/Build'))
const Whitelabel = lazy(() => import('./pages/Whitelabel'))
const Deploy = lazy(() => import('./pages/Deploy'))
const Changelog = lazy(() => import('./pages/Changelog'))
const Blog = lazy(() => import('./pages/Blog'))
const BlogArticle = lazy(() => import('./pages/BlogArticle'))
const Integrations = lazy(() => import('./pages/Integrations'))

import './App.css'

const RouteFallback = () => (
  <div className="routeFallback">
    <div className="routeFallback__spinner" />
  </div>
)

let appMounted = false

const RoutePage = ({ children }) => (
  <ErrorBoundary>
    <Suspense fallback={<RouteFallback />}>
      <motion.div
        initial={appMounted ? { opacity: 0, y: 10 } : false}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
      >
        {children}
      </motion.div>
    </Suspense>
  </ErrorBoundary>
)

function App() {
  const location = useLocation()

  useEffect(() => {
    appMounted = true
  }, [])

  const showPromo =
    ['/', '/pricing', '/products', '/blog', '/integrations', '/company', '/demo/schedule'].includes(
      location.pathname
    ) || /^\/products\/.+/.test(location.pathname)

  return (
    <ErrorBoundary>
      <ToastProvider>
        <div className="app-wrapper">
          <a href="#main" className="skip-link">
            Skip to content
          </a>
          <ScrollToTop />
          <AnchorScrollHandler />
          <LiveChat />
          <CookieConsent />
          {showPromo && <PromoBanner />}
          <SEORouter />
          <Navbar />
          <main id="main">
            <AnimatePresence mode="wait">
              <Routes location={location} key={location.pathname}>
                <Route
                  path="/"
                  element={
                    <RoutePage>
                      <Home />
                    </RoutePage>
                  }
                />
                <Route
                  path="/pricing"
                  element={
                    <RoutePage>
                      <Pricing />
                    </RoutePage>
                  }
                />
                <Route
                  path="/products"
                  element={
                    <RoutePage>
                      <Products />
                    </RoutePage>
                  }
                />
                <Route
                  path="/products/:slug"
                  element={
                    <RoutePage>
                      <ProductPricing />
                    </RoutePage>
                  }
                />
                <Route
                  path="/demo/:productId/:moduleId?"
                  element={
                    <RoutePage>
                      <AuthGuard>
                        <Demo />
                      </AuthGuard>
                    </RoutePage>
                  }
                />
                <Route
                  path="/demo/schedule"
                  element={
                    <RoutePage>
                      <DemoSchedule />
                    </RoutePage>
                  }
                />
                <Route
                  path="/checkout"
                  element={
                    <RoutePage>
                      <AuthGuard>
                        <Checkout />
                      </AuthGuard>
                    </RoutePage>
                  }
                />
                <Route
                  path="/payment-success"
                  element={
                    <RoutePage>
                      <PaymentSuccess />
                    </RoutePage>
                  }
                />
                <Route
                  path="/payment-failed"
                  element={
                    <RoutePage>
                      <PaymentFailed />
                    </RoutePage>
                  }
                />
                <Route
                  path="/account"
                  element={
                    <RoutePage>
                      <AuthGuard>
                        <Account />
                      </AuthGuard>
                    </RoutePage>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <RoutePage>
                      <AuthGuard>
                        <Dashboard />
                      </AuthGuard>
                    </RoutePage>
                  }
                />
                <Route
                  path="/support"
                  element={
                    <RoutePage>
                      <Support />
                    </RoutePage>
                  }
                />
                <Route
                  path="/support/tickets"
                  element={
                    <RoutePage>
                      <AuthGuard>
                        <Support />
                      </AuthGuard>
                    </RoutePage>
                  }
                />
                <Route
                  path="/support/tickets/new"
                  element={
                    <RoutePage>
                      <AuthGuard>
                        <Support />
                      </AuthGuard>
                    </RoutePage>
                  }
                />
                <Route
                  path="/support/tickets/:id"
                  element={
                    <RoutePage>
                      <AuthGuard>
                        <Support />
                      </AuthGuard>
                    </RoutePage>
                  }
                />
                <Route
                  path="/support/admin"
                  element={
                    <RoutePage>
                      <AdminGuard role="support">
                        <SupportAdmin />
                      </AdminGuard>
                    </RoutePage>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <RoutePage>
                      <AdminGuard role="admin">
                        <Admin />
                      </AdminGuard>
                    </RoutePage>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <RoutePage>
                      <AuthGuard>
                        <Notifications />
                      </AuthGuard>
                    </RoutePage>
                  }
                />
                <Route
                  path="/marketplace"
                  element={
                    <RoutePage>
                      <AuthGuard>
                        <Marketplace />
                      </AuthGuard>
                    </RoutePage>
                  }
                />
                <Route
                  path="/marketplace/:slug"
                  element={
                    <RoutePage>
                      <AuthGuard>
                        <MarketplaceDetail />
                      </AuthGuard>
                    </RoutePage>
                  }
                />
                <Route
                  path="/wishlist"
                  element={
                    <RoutePage>
                      <AuthGuard>
                        <Wishlist />
                      </AuthGuard>
                    </RoutePage>
                  }
                />
                <Route
                  path="/cart"
                  element={
                    <RoutePage>
                      <AuthGuard>
                        <Cart />
                      </AuthGuard>
                    </RoutePage>
                  }
                />
                <Route
                  path="/marketplace/publish"
                  element={
                    <RoutePage>
                      <AuthGuard>
                        <Publish />
                      </AuthGuard>
                    </RoutePage>
                  }
                />
                <Route
                  path="/marketplace/publisher"
                  element={
                    <RoutePage>
                      <AuthGuard>
                        <Publisher />
                      </AuthGuard>
                    </RoutePage>
                  }
                />
                <Route
                  path="/build"
                  element={
                    <RoutePage>
                      <Build />
                    </RoutePage>
                  }
                />
                <Route
                  path="/whitelabel"
                  element={
                    <RoutePage>
                      <Whitelabel />
                    </RoutePage>
                  }
                />
                <Route
                  path="/deploy"
                  element={
                    <RoutePage>
                      <Deploy />
                    </RoutePage>
                  }
                />
                <Route
                  path="/login"
                  element={
                    <RoutePage>
                      <RedirectIfAuthed>
                        <Login />
                      </RedirectIfAuthed>
                    </RoutePage>
                  }
                />
                <Route
                  path="/login/admin"
                  element={
                    <RoutePage>
                      <LoginAdmin />
                    </RoutePage>
                  }
                />
                <Route
                  path="/signup"
                  element={
                    <RoutePage>
                      <RedirectIfAuthed>
                        <Signup />
                      </RedirectIfAuthed>
                    </RoutePage>
                  }
                />
                <Route
                  path="/forgot-password"
                  element={
                    <RoutePage>
                      <ForgotPassword />
                    </RoutePage>
                  }
                />
                <Route
                  path="/reset-password"
                  element={
                    <RoutePage>
                      <ResetPassword />
                    </RoutePage>
                  }
                />
                <Route
                  path="/company"
                  element={
                    <RoutePage>
                      <Company />
                    </RoutePage>
                  }
                />
                <Route
                  path="/changelog"
                  element={
                    <RoutePage>
                      <Changelog />
                    </RoutePage>
                  }
                />
                <Route
                  path="/blog"
                  element={
                    <RoutePage>
                      <Blog />
                    </RoutePage>
                  }
                />
                <Route
                  path="/blog/:slug"
                  element={
                    <RoutePage>
                      <BlogArticle />
                    </RoutePage>
                  }
                />
                <Route
                  path="/integrations"
                  element={
                    <RoutePage>
                      <Integrations />
                    </RoutePage>
                  }
                />
                <Route
                  path="/privacy-policy"
                  element={
                    <RoutePage>
                      <Legal title="Privacy Policy" />
                    </RoutePage>
                  }
                />
                <Route
                  path="/terms-of-service"
                  element={
                    <RoutePage>
                      <Legal title="Terms of Service" />
                    </RoutePage>
                  }
                />
                <Route
                  path="*"
                  element={
                    <RoutePage>
                      <NotFound />
                    </RoutePage>
                  }
                />
              </Routes>
            </AnimatePresence>
          </main>
          <Footer />
        </div>
      </ToastProvider>
    </ErrorBoundary>
  )
}

export default App
